/**
 * RequireDesktop.tsx
 *
 * Fails gracefully on the surfaces that are not being made responsive: the canvas
 * editors and the two builder wizards. Below `lg` they show a notice instead of a
 * canvas that cannot work at that width.
 *
 * Named for what it does rather than for its fallback, joining `RequireModule` and
 * `RequirePermission` — like them, it renders its children almost all of the time.
 *
 * ── Sampled once, never re-read ─────────────────────────────────────────────────
 * `useDesktopGate` latches the answer at mount with the one-shot breakpoint read, not
 * the live subscription. A live read would unmount an editor the moment someone rotated
 * a tablet, taking unsaved work with it. That is also why the value is held in a
 * `useState` initialiser rather than recomputed: it must not change for the lifetime of
 * the component.
 *
 * ── Two shapes, because the call sites differ ───────────────────────────────────
 * `RequireDesktop` wraps a route element. The editors instead call `useDesktopGate` and
 * return `DesktopOnlyNotice` themselves, because their entry points are lazy imports
 * inside page files, and a wrapper would have to live in one of those.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor } from 'lucide-react';
import { BREAKPOINTS, isBelowNow } from '@/hooks/useBreakpoint';
import { EmptyState, type EmptyStateAction } from '@/components/ui/EmptyState';

/** The width below which these surfaces decline to render. */
const GATE: keyof typeof BREAKPOINTS = 'lg';

/**
 * True when the viewport was narrower than `lg` **at mount**. Deliberately does not
 * react to resizing — see the note above.
 */
export function useDesktopGate(): boolean {
  const [tooNarrow] = useState(() => isBelowNow(GATE));
  return tooNarrow;
}

export interface DesktopOnlyNoticeProps {
  /** What is unavailable, named as the user would say it: "The email editor". */
  what: string;
  /**
   * Somewhere the user can go that does work at this width, offered as the primary
   * action.
   *
   * Optional because the three canvas editors have no narrow-screen equivalent to point
   * at — for them the notice is unchanged. The report builder does: SCRUM-1472 asked
   * whether the gate was intended and, if so, for a way out other than "Go back", and
   * `/reports/my-reports` is already ungated and already this section's route fallback.
   */
  alternative?: { label: string; to: string };
}

/** The notice itself, so an editor can return it directly from its own render. */
export function DesktopOnlyNotice({ what, alternative }: DesktopOnlyNoticeProps) {
  const navigate = useNavigate();

  const goBack: EmptyStateAction = {
    label: 'Go back',
    variant: 'secondary',
    onClick: () => navigate(-1),
  };

  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center p-6">
      <EmptyState
        icon={<Monitor className="h-10 w-10 text-slate-300" />}
        title={`${what} needs a wider screen`}
        description={`It is a drag-and-drop canvas, and it has not been built to work below ${BREAKPOINTS[GATE]}px. Open this page on a desktop browser or a wider window.`}
        // With an alternative, it leads and "Go back" becomes secondary — the point of
        // the ticket is that "Go back" alone is a dead end. Without one, "Go back" stays
        // exactly where it was, in the same slot with the same variant.
        action={
          alternative
            ? { label: alternative.label, onClick: () => navigate(alternative.to) }
            : goBack
        }
        secondaryAction={alternative ? goBack : undefined}
      />
    </div>
  );
}

export interface RequireDesktopProps extends DesktopOnlyNoticeProps {
  children: ReactNode;
}

/** Wraps a route element. Below `lg` the child never mounts. */
export function RequireDesktop({ what, alternative, children }: RequireDesktopProps) {
  const tooNarrow = useDesktopGate();

  if (tooNarrow) return <DesktopOnlyNotice what={what} alternative={alternative} />;

  return <>{children}</>;
}
